import type { Facing } from '../contracts/presentation-snapshot';
import type { BufferedAction } from '../input/input-frame';
import type { LocalAabb, WorldAabb } from '../physics/aabb';
import { assertLocalAabb, toWorldAabb } from '../physics/aabb';
import type { Vec2 } from '../physics/vec2';
import { assertFiniteNumber, assertFiniteVec2 } from '../physics/vec2';
import { assertNonNegativeInteger } from '../shared/invariants';

export const ATTACK_TYPES = ['NORMAL', 'SPECIAL', 'PROJECTILE', 'SUPER'] as const;
export type AttackType = (typeof ATTACK_TYPES)[number];
export const RANGE_TYPES = ['CLOSE', 'FAR'] as const;
export type RangeType = (typeof RANGE_TYPES)[number];
export type AttackUsage = BufferedAction | 'COUNTER_RESPONSE' | 'TRAP';
export const CANCEL_CONDITIONS = ['ALWAYS', 'ON_HIT', 'ON_BLOCK'] as const;
export type CancelCondition = (typeof CANCEL_CONDITIONS)[number];
export const EFFECT_TYPES = ['TIME_PENALTY', 'SLOW', 'SPAWN_TRAP', 'COUNTER_WINDOW'] as const;
export type EffectType = (typeof EFFECT_TYPES)[number];
export const EFFECT_TRIGGERS = ['ON_START', 'ON_FRAME', 'ON_HIT', 'ON_BLOCK', 'ON_COUNTER'] as const;
export type EffectTrigger = (typeof EFFECT_TRIGGERS)[number];

export interface GaugeGainSettings {
  readonly attackerOnHit: number;
  readonly attackerOnBlock: number;
  readonly defenderOnHit: number;
  readonly defenderOnBlock: number;
}

export interface AttackHitboxDefinition {
  readonly hitId: string;
  readonly box: LocalAabb;
  readonly startFrame?: number;
  readonly endFrame?: number;
  readonly damage?: number;
  readonly chipDamage?: number;
  readonly hitstunFrames?: number;
  readonly blockstunFrames?: number;
  readonly hitstopFrames?: number;
  readonly blockHitstopFrames?: number;
  readonly knockback?: Vec2;
  readonly causesKnockdown?: boolean;
  readonly gaugeGain?: GaugeGainSettings;
}

export interface AttackMovementWindow {
  readonly startFrame: number;
  readonly endFrame: number;
  readonly displacementPerFrame: Vec2;
}

export interface CancelRoute {
  readonly targetAttackId: string;
  readonly startFrame: number;
  readonly endFrame: number;
  readonly condition: CancelCondition;
}

export interface InvulnerabilityWindow {
  readonly startFrame: number;
  readonly endFrame: number;
}

export interface GenericTrapDefinition {
  readonly id: string;
  readonly attackId: string;
  readonly hitbox: LocalAabb;
  readonly lifetimeFrames: number;
  readonly offset: Vec2;
  readonly maxActivePerOwner?: number;
}

export interface EffectDefinition {
  readonly id: string;
  readonly type: EffectType;
  readonly trigger: EffectTrigger;
  readonly value?: number;
  readonly durationFrames?: number;
  readonly strength?: number;
  readonly frame?: number;
  readonly oncePerCombo?: boolean;
  readonly responseAttackId?: string;
  readonly trapDefinitionId?: string;
  readonly minimumTimerFrames?: number;
}

export interface AttackDefinition {
  readonly id: string;
  readonly name: string;
  readonly attackType: AttackType;
  readonly rangeType: RangeType;
  readonly usage: AttackUsage;
  readonly counterable: boolean;
  readonly guardable: boolean;
  readonly startupFrames: number;
  readonly activeFrames: number;
  readonly recoveryFrames: number;
  readonly damage: number;
  readonly chipDamage: number;
  readonly hitstunFrames: number;
  readonly blockstunFrames: number;
  readonly hitstopFrames: number;
  readonly blockHitstopFrames: number;
  readonly knockback: Vec2;
  readonly causesKnockdown: boolean;
  readonly gaugeCost: number;
  readonly gaugeGain: GaugeGainSettings;
  readonly movement: Vec2;
  readonly hitboxes: readonly AttackHitboxDefinition[];
  readonly cancelRoutes: readonly CancelRoute[];
  readonly effects: readonly EffectDefinition[];
  readonly invulnerability: readonly InvulnerabilityWindow[];
  readonly cinematicId: string | null;
  readonly airOnly?: boolean;
  readonly landingRecoveryFrames?: number;
  readonly movementWindows?: readonly AttackMovementWindow[];
}

export interface AttackInstance {
  readonly attackInstanceId: string;
  readonly attackId: string;
  readonly id: string;
  readonly attackerId: string;
  elapsedFrames: number;
  readonly startFacing: Facing;
  hitRecord: Set<string>;
  readonly startedLogicalFrame: number;
  justStarted: boolean;
  confirmedHit: boolean;
  confirmedBlock: boolean;
}

export type AttackPhase = 'STARTUP' | 'ACTIVE' | 'RECOVERY' | 'COMPLETE';

export interface ProjectedAttackHitbox {
  readonly attackInstanceId: string;
  readonly attackId: string;
  readonly attackerId: string;
  readonly hitId: string;
  readonly box: WorldAabb;
}

const assertEnum = (value: string, allowed: readonly string[], name: string): void => {
  if (!allowed.includes(value)) throw new RangeError(`${name} has an invalid value.`);
};

export const getAttackTotalFrames = (definition: AttackDefinition): number =>
  definition.startupFrames + definition.activeFrames + definition.recoveryFrames;

export const getAttackPhase = (definition: AttackDefinition, elapsedFrames: number): AttackPhase => {
  assertNonNegativeInteger(elapsedFrames, 'elapsedFrames');
  if (elapsedFrames < definition.startupFrames) return 'STARTUP';
  if (elapsedFrames < definition.startupFrames + definition.activeFrames) return 'ACTIVE';
  if (elapsedFrames < getAttackTotalFrames(definition)) return 'RECOVERY';
  return 'COMPLETE';
};

export const assertAttackDefinition = (definition: AttackDefinition, name = 'attack'): void => {
  if (definition.id.length === 0 || definition.name.length === 0) throw new RangeError(`${name} ids/names must not be empty.`);
  assertEnum(definition.attackType, ATTACK_TYPES, `${name}.attackType`);
  assertEnum(definition.rangeType, RANGE_TYPES, `${name}.rangeType`);
  for (const field of ['startupFrames', 'activeFrames', 'recoveryFrames', 'hitstunFrames', 'blockstunFrames',
    'hitstopFrames', 'blockHitstopFrames'] as const) assertNonNegativeInteger(definition[field], `${name}.${field}`);
  for (const field of ['damage', 'chipDamage', 'gaugeCost'] as const) {
    assertFiniteNumber(definition[field], `${name}.${field}`);
    if (definition[field] < 0) throw new RangeError(`${name}.${field} must be non-negative.`);
  }
  if (definition.attackType === 'SUPER' && definition.gaugeCost !== 100) {
    throw new RangeError(`${name} SUPER gaugeCost must be exactly 100.`);
  }
  assertFiniteVec2(definition.knockback, `${name}.knockback`);
  assertFiniteVec2(definition.movement, `${name}.movement`);
  for (const [key, value] of Object.entries(definition.gaugeGain) as [keyof GaugeGainSettings, number][]) {
    assertFiniteNumber(value, `${name}.gaugeGain.${key}`);
  }
  const hitIds = new Set<string>();
  for (const hitbox of definition.hitboxes) {
    if (hitbox.hitId.length === 0 || hitIds.has(hitbox.hitId)) throw new RangeError(`${name} hitId must be unique and non-empty.`);
    hitIds.add(hitbox.hitId);
    assertLocalAabb(hitbox.box, `${name}.hitbox.${hitbox.hitId}`);
    if (hitbox.startFrame !== undefined) assertNonNegativeInteger(hitbox.startFrame, `${name}.hitbox.startFrame`);
    if (hitbox.endFrame !== undefined) assertNonNegativeInteger(hitbox.endFrame, `${name}.hitbox.endFrame`);
    if (hitbox.startFrame !== undefined && hitbox.endFrame !== undefined && hitbox.endFrame < hitbox.startFrame) {
      throw new RangeError(`${name} hitbox endFrame must not precede startFrame.`);
    }
    for (const field of ['damage', 'chipDamage'] as const) {
      const value = hitbox[field];
      if (value !== undefined) {
        assertFiniteNumber(value, `${name}.hitbox.${field}`);
        if (value < 0) throw new RangeError(`${name}.hitbox.${field} must be non-negative.`);
      }
    }
    for (const field of ['hitstunFrames', 'blockstunFrames', 'hitstopFrames', 'blockHitstopFrames'] as const) {
      const value = hitbox[field];
      if (value !== undefined) assertNonNegativeInteger(value, `${name}.hitbox.${field}`);
    }
    if (hitbox.knockback !== undefined) assertFiniteVec2(hitbox.knockback, `${name}.hitbox.knockback`);
    if (hitbox.gaugeGain !== undefined) {
      for (const [key, value] of Object.entries(hitbox.gaugeGain) as [keyof GaugeGainSettings, number][]) {
        assertFiniteNumber(value, `${name}.hitbox.gaugeGain.${key}`);
      }
    }
  }
  for (const route of definition.cancelRoutes) {
    assertNonNegativeInteger(route.startFrame, `${name}.cancel.startFrame`);
    assertNonNegativeInteger(route.endFrame, `${name}.cancel.endFrame`);
    if (route.endFrame < route.startFrame || route.targetAttackId.length === 0) throw new RangeError(`${name} cancel route is invalid.`);
    assertEnum(route.condition, CANCEL_CONDITIONS, `${name}.cancel.condition`);
  }
  for (const window of definition.invulnerability) {
    assertNonNegativeInteger(window.startFrame, `${name}.invulnerability.startFrame`);
    assertNonNegativeInteger(window.endFrame, `${name}.invulnerability.endFrame`);
    if (window.endFrame < window.startFrame) throw new RangeError(`${name} invulnerability window is invalid.`);
  }
  for (const effect of definition.effects) {
    if (effect.id.length === 0) throw new RangeError(`${name} effect id must not be empty.`);
    assertEnum(effect.type, EFFECT_TYPES, `${name}.effect.type`);
    assertEnum(effect.trigger, EFFECT_TRIGGERS, `${name}.effect.trigger`);
    if (effect.durationFrames !== undefined) assertNonNegativeInteger(effect.durationFrames, `${name}.effect.durationFrames`);
    if (effect.frame !== undefined) assertNonNegativeInteger(effect.frame, `${name}.effect.frame`);
    if (effect.value !== undefined) assertFiniteNumber(effect.value, `${name}.effect.value`);
    if (effect.strength !== undefined) assertFiniteNumber(effect.strength, `${name}.effect.strength`);
    if (effect.minimumTimerFrames !== undefined) assertNonNegativeInteger(effect.minimumTimerFrames, `${name}.effect.minimumTimerFrames`);
  }
  if (definition.landingRecoveryFrames !== undefined) {
    assertNonNegativeInteger(definition.landingRecoveryFrames, `${name}.landingRecoveryFrames`);
  }
  for (const window of definition.movementWindows ?? []) {
    assertNonNegativeInteger(window.startFrame, `${name}.movementWindow.startFrame`);
    assertNonNegativeInteger(window.endFrame, `${name}.movementWindow.endFrame`);
    if (window.endFrame < window.startFrame) throw new RangeError(`${name} movement window is invalid.`);
    assertFiniteVec2(window.displacementPerFrame, `${name}.movementWindow.displacementPerFrame`);
  }
};

export const cloneAttackDefinition = (definition: AttackDefinition): AttackDefinition => {
  assertAttackDefinition(definition);
  return Object.freeze({
    ...definition,
    knockback: Object.freeze({ ...definition.knockback }),
    gaugeGain: Object.freeze({ ...definition.gaugeGain }),
    movement: Object.freeze({ ...definition.movement }),
    hitboxes: Object.freeze(definition.hitboxes.map((hitbox) => Object.freeze({
      ...hitbox,
      box: Object.freeze({ ...hitbox.box }),
      ...(hitbox.knockback === undefined ? {} : { knockback: Object.freeze({ ...hitbox.knockback }) }),
      ...(hitbox.gaugeGain === undefined ? {} : { gaugeGain: Object.freeze({ ...hitbox.gaugeGain }) }),
    }))),
    cancelRoutes: Object.freeze(definition.cancelRoutes.map((route) => Object.freeze({ ...route }))),
    effects: Object.freeze(definition.effects.map((effect) => Object.freeze({ ...effect }))),
    invulnerability: Object.freeze(definition.invulnerability.map((window) => Object.freeze({ ...window }))),
    ...(definition.movementWindows === undefined ? {} : {
      movementWindows: Object.freeze(definition.movementWindows.map((window) => Object.freeze({
        ...window,
        displacementPerFrame: Object.freeze({ ...window.displacementPerFrame }),
      }))),
    }),
  });
};

export const resolveAttackForHit = (
  definition: AttackDefinition,
  hitId: string,
): AttackDefinition => {
  const hitbox = definition.hitboxes.find((candidate) => candidate.hitId === hitId);
  if (hitbox === undefined) return definition;
  return {
    ...definition,
    damage: hitbox.damage ?? definition.damage,
    chipDamage: hitbox.chipDamage ?? definition.chipDamage,
    hitstunFrames: hitbox.hitstunFrames ?? definition.hitstunFrames,
    blockstunFrames: hitbox.blockstunFrames ?? definition.blockstunFrames,
    hitstopFrames: hitbox.hitstopFrames ?? definition.hitstopFrames,
    blockHitstopFrames: hitbox.blockHitstopFrames ?? definition.blockHitstopFrames,
    knockback: hitbox.knockback ?? definition.knockback,
    causesKnockdown: hitbox.causesKnockdown ?? definition.causesKnockdown,
    gaugeGain: hitbox.gaugeGain ?? definition.gaugeGain,
  };
};

export const assertTrapDefinition = (definition: GenericTrapDefinition, name = 'trap'): void => {
  if (definition.id.length === 0 || definition.attackId.length === 0) throw new RangeError(`${name} ids must not be empty.`);
  assertLocalAabb(definition.hitbox, `${name}.hitbox`);
  assertNonNegativeInteger(definition.lifetimeFrames, `${name}.lifetimeFrames`);
  if (definition.lifetimeFrames === 0) throw new RangeError(`${name}.lifetimeFrames must be positive.`);
  assertFiniteVec2(definition.offset, `${name}.offset`);
  if (definition.maxActivePerOwner !== undefined) {
    assertNonNegativeInteger(definition.maxActivePerOwner, `${name}.maxActivePerOwner`);
    if (definition.maxActivePerOwner === 0) throw new RangeError(`${name}.maxActivePerOwner must be positive.`);
  }
};

export const createAttackInstance = (
  definition: AttackDefinition,
  attackInstanceId: string,
  attackerId: string,
  startFacing: Facing,
  logicalFrame: number,
): AttackInstance => {
  assertAttackDefinition(definition);
  assertNonNegativeInteger(logicalFrame, 'logicalFrame');
  if (attackInstanceId.length === 0 || attackerId.length === 0) throw new RangeError('Attack instance ids must not be empty.');
  return {
    attackInstanceId,
    attackId: definition.id,
    id: definition.id,
    attackerId,
    elapsedFrames: 0,
    startFacing,
    hitRecord: new Set(),
    startedLogicalFrame: logicalFrame,
    justStarted: true,
    confirmedHit: false,
    confirmedBlock: false,
  };
};

export const cloneAttackInstance = (instance: Readonly<AttackInstance>): AttackInstance => ({
  attackInstanceId: instance.attackInstanceId,
  attackId: instance.attackId,
  id: instance.id,
  attackerId: instance.attackerId,
  elapsedFrames: instance.elapsedFrames,
  startFacing: instance.startFacing,
  hitRecord: new Set(instance.hitRecord),
  startedLogicalFrame: instance.startedLogicalFrame,
  justStarted: instance.justStarted,
  confirmedHit: instance.confirmedHit,
  confirmedBlock: instance.confirmedBlock,
});

export const projectActiveAttackHitboxes = (
  definition: AttackDefinition,
  instance: Readonly<AttackInstance>,
  root: Vec2,
): readonly ProjectedAttackHitbox[] => {
  if (getAttackPhase(definition, instance.elapsedFrames) !== 'ACTIVE') return Object.freeze([]);
  const defaultStart = definition.startupFrames;
  const defaultEnd = definition.startupFrames + definition.activeFrames - 1;
  return Object.freeze(definition.hitboxes
    .filter((hitbox) => instance.elapsedFrames >= (hitbox.startFrame ?? defaultStart)
      && instance.elapsedFrames <= (hitbox.endFrame ?? defaultEnd))
    .map((hitbox) => Object.freeze({
      attackInstanceId: instance.attackInstanceId,
      attackId: definition.id,
      attackerId: instance.attackerId,
      hitId: hitbox.hitId,
      box: toWorldAabb(hitbox.box, root, instance.startFacing),
    })));
};

export const isAttackInvulnerable = (definition: AttackDefinition, elapsedFrames: number): boolean =>
  definition.invulnerability.some((window) => elapsedFrames >= window.startFrame && elapsedFrames <= window.endFrame);

export const makeHitRecordKey = (attackInstanceId: string, hitId: string, defenderId: string): string =>
  `${attackInstanceId}\u0000${hitId}\u0000${defenderId}`;

export const isCancelRouteEligible = (
  route: CancelRoute,
  elapsedFrames: number,
  confirmedHit: boolean,
  confirmedBlock: boolean,
): boolean => elapsedFrames >= route.startFrame && elapsedFrames <= route.endFrame
  && (route.condition === 'ALWAYS'
    || (route.condition === 'ON_HIT' && confirmedHit)
    || (route.condition === 'ON_BLOCK' && confirmedBlock));
